// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useMemo } from 'react';

import { api } from '../../api/client';
import type { BuildingElement } from '../../api/types';
import { isTerminal } from '../../api/types';
import { useJobs } from '../../app/JobsContext';
import { useAsync } from '../../app/useAsync';
import { elementsFromModel, type ElementModelSource } from '../../bygning/elementGeometry';

/**
 * The building model's elements as solids for the Ground's 3D model
 * (frontend-r22.27): `GET /elements` — each element's own mesh from the
 * solids stage — re-read when a job finishes. A server without the route,
 * or a project whose solids stage has not run (`[]`), falls back to solids
 * built from the rows the Ground already has (`elementsFromModel`). Null
 * until either is known.
 */
export function useBuildingElements(rows: ElementModelSource): BuildingElement[] | null {
  const { jobs } = useJobs();
  const finished = jobs.filter((j) => isTerminal(j.status)).length;
  const read = useAsync(
    async (signal) => {
      try {
        return await api.elements(signal);
      } catch (cause) {
        if (signal.aborted) throw cause;
        return null;
      }
    },
    [finished],
  );
  const { walls, openings, spaces, slabs } = rows;
  return useMemo(() => {
    if (read.data === undefined) return null;
    if (read.data && read.data.length > 0) return read.data;
    if (!walls && !spaces && !slabs) return read.data === null ? null : [];
    return elementsFromModel({ walls, openings, spaces, slabs });
  }, [read.data, walls, openings, spaces, slabs]);
}
